import { useState, useEffect } from 'react'
import { Moon, Sun, Monitor, Check } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Theme, getTheme, setTheme } from '@/lib/theme'

interface ThemeModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ThemeModal({ open, onOpenChange }: ThemeModalProps) {
  const [selectedTheme, setSelectedTheme] = useState<Theme>('system')

  useEffect(() => {
    // Load saved theme preference
    setSelectedTheme(getTheme())
  }, [open])

  const applyTheme = (theme: Theme) => {
    setSelectedTheme(theme)
    setTheme(theme)
  }

  const themes = [
    {
      value: 'light' as Theme,
      label: 'Light',
      description: 'Light mode',
      icon: Sun,
    },
    {
      value: 'dark' as Theme,
      label: 'Dark',
      description: 'Dark mode',
      icon: Moon,
    },
    {
      value: 'system' as Theme,
      label: 'System',
      description: 'Use system preference',
      icon: Monitor,
    },
  ]

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Theme</DialogTitle>
          <DialogDescription>
            Choose how KLASSIC looks to you. Select a single theme or sync with your system.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-4">
          {themes.map((theme) => {
            const Icon = theme.icon
            const isSelected = selectedTheme === theme.value

            return (
              <button
                key={theme.value}
                onClick={() => applyTheme(theme.value)}
                className={`
                  w-full flex items-center gap-4 p-4 rounded-lg border-2 transition-all
                  ${
                    isSelected
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50 hover:bg-accent/50'
                  }
                `}
              >
                <div
                  className={`
                  flex h-10 w-10 items-center justify-center rounded-lg
                  ${isSelected ? 'bg-primary text-primary-foreground' : 'bg-accent'}
                `}
                >
                  <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium">{theme.label}</p>
                  <p className="text-sm text-muted-foreground">{theme.description}</p>
                </div>
                {isSelected && (
                  <Check className="h-5 w-5 text-primary" />
                )}
              </button>
            )
          })}
        </div>

        <div className="flex justify-end pt-4 border-t">
          <Button onClick={() => onOpenChange(false)}>Done</Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
